import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { SlideOutWrapper } from './SlideOutWrapper';
import {
    buildVehicleForSimilaritySearch,
    ChangeVehicleActivity,
    changeVehicleCreators
} from '@makemydeal/dr-activities-change-vehicle';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { ThemeProvider } from 'styled-components';
import { FULL_SCREEN_SLIDE_OUT_WIDTH } from '@makemydeal/dr-shared-ui-utils';
import type { SlideOutComponentProps } from '../../types';

/** First Pencil's Change Vehicle — stores the pick without applying it to the deal. */
export const FirstPencilChangeVehicleSlideOut = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const selectedVehicle = useSelector(firstPencilSelectors.getSelectedVehicle);

    // Seeds AI similarity search from the pick rather than the deal's own (uncommitted) vehicle.
    const similaritySearchVehicleOverride = useMemo(
        () => (selectedVehicle ? buildVehicleForSimilaritySearch(selectedVehicle) : undefined),
        [selectedVehicle]
    );

    const handleVehicleSelected = (vehicle: IVehicle) => {
        dispatch(firstPencilActionCreators.vehicleSelected(vehicle));
        // Resets Change Vehicle's own selection state, or the same row on reopen would no-op.
        dispatch(changeVehicleCreators.vehicleChangeCancel());
        onClose();
    };

    return (
        <SlideOutWrapper
            title={'Change Vehicle'}
            isOpen={isOpen}
            onClose={onClose}
            panelWidth={FULL_SCREEN_SLIDE_OUT_WIDTH}
            testId="tab-slideout-first-pencil-change-vehicle"
            vehicleOverride={selectedVehicle}
        >
            <ThemeProvider theme={theme}>
                <ChangeVehicleActivity
                    isInsideSlideOut={true}
                    showReturnToOffer={false}
                    // Nothing is applied here, so there's nothing for the retention modal to carry over.
                    enableConfirmModal={false}
                    onCancel={onClose}
                    onVehicleSelected={handleVehicleSelected}
                    similaritySearchVehicleOverride={similaritySearchVehicleOverride}
                />
            </ThemeProvider>
        </SlideOutWrapper>
    );
};
